import {applyTransaction} from "@datorama/akita"
import API from "api/API"
import Logger from "logger/logger"
import {useEffect, useState} from "react"
import {Subscription} from 'rxjs'
import {authQuery, rootAuthStore} from "store/auth/auth.store"
import {envQuery} from "store/system/environment/env.store"
import {onEmit} from "utils/onEmit"

export const subscribeAuth = {
  isLoading: 'isLoading',
  isAuthenticated: "isAuthenticated",
  token: 'token'
}

const useAuthStore = (subscribe: string[], caller: string) => {

  const subscribed: Subscription[] = []

  const [isLoading, handleIsLoading] = useState<boolean>()
  const [isAuthenticated, setAuthenticated] = useState<boolean>()
  const [token, setToken] = useState<string>()

  useEffect(() => {
    if (!subscribe) throw Error("Service.Auth: Define what you want to subscribe to")
    if (!caller) throw Error("Service.Auth: Define where you are calling from")

    subscribe.map(s => {
      if (!Object.keys(subscribeAuth).includes(s))
        throw new Error(`Service.Auth: Can't subscribe to: ${s}`)
    })

    Logger(`Service.Auth.init.${caller}`, subscribe.join(', '))

    if (subscribe.includes(subscribeAuth.isLoading))
      subscribeLoading()

    if (subscribe.includes(subscribeAuth.isAuthenticated))
      subscribeAuthenticated()

    if (subscribe.includes(subscribeAuth.token))
      subscribeToken()

    return () => {
      Logger(`Service.Auth.unloading.${caller}`)
      subscribed.map(s => s.unsubscribe())
    }
  }, [])

  const login = async (email: string, password: string) => {
    rootAuthStore.setLoading(true)
    const post = await API.post(`auth/login`, {email, password})
    applyTransaction(() => {
      rootAuthStore.setLoading(false)
      if (post.data.token)
        rootAuthStore.update(state => {
          state.token = post.data.token
          state.isAuthenticated = true
        })
    })
  }

  const forgotPasswordMail = async (email: string) => {
    const language = envQuery.getValue().language
    await API.post('/auth/password/request', {email, language})
  }

  const verifyResetPasswordToken = async (token: string) => {
    try {
      await API.post('/auth/password/verify-token', {token})
      return true
    } catch (e) {
      return false
    }
  }

  const resetPassword = async (password: string, token: string) => {
    await API.post('/auth/password/reset', {password, token})
  }

  return {
    isLoadingAuth: isLoading,
    isAuthenticated,
    token,
    login,
    forgotPasswordMail,
    verifyResetPasswordToken,
    resetPassword,
  }

  function subscribeLoading() {
    subscribed.push(onEmit(authQuery.selectLoading(), newLoading => {
      handleIsLoading(newLoading)
    }))
  }

  function subscribeAuthenticated() {
    subscribed.push(onEmit(authQuery.select('isAuthenticated'), newAuth => {
      setAuthenticated(newAuth)
    }))
  }

  function subscribeToken() {
    subscribed.push(onEmit(authQuery.select('token'), token => {
      setToken(token)
    }))
  }
}

export default useAuthStore
